import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { MetricShiftConfig, WidgetConfig } from '../types';
import { eventBus } from '../engine/EventBus';
import { TriggerEngine } from '../engine/TriggerEngine';

// --- Context ---

interface MetricShiftContextValue {
  config: MetricShiftConfig;
  isOpen: boolean;
  openWidget: (flowId?: string) => void;
  closeWidget: () => void;
}

const MetricShiftContext = createContext<MetricShiftContextValue | null>(null);

export const useMetricShift = (): MetricShiftContextValue => {
  const ctx = useContext(MetricShiftContext);
  if (!ctx) {
    throw new Error('useMetricShift must be used within MetricShiftProvider');
  }
  return ctx;
};

// --- Global imperative API ---
// Lets developers call MetricShift.open() outside the React tree

interface MetricShiftGlobal {
  init: (config: MetricShiftConfig) => void;
  open: (flowId?: string) => void;
  close: () => void;
  trigger: (eventName: string) => void;
  getConfig: () => MetricShiftConfig | null;
}

let _globalConfig: MetricShiftConfig | null = null;

export const MetricShift: MetricShiftGlobal = {
  init: (config: MetricShiftConfig) => {
    _globalConfig = {
      apiUrl: 'https://metricshift-sdk-be-production.up.railway.app',
      ...config,
    };
    console.log('✅ MetricShift SDK initialised', {
      projectId: config.projectId,
      apiUrl: _globalConfig.apiUrl,
    });
  },

  open: (flowId?: string) => {
    eventBus.emit({ type: 'open', payload: { flowId } });
  },

  close: () => {
    eventBus.emit({ type: 'close' });
  },

  trigger: (eventName: string) => {
    eventBus.emit({ type: 'trigger', payload: { eventName } });
  },

  getConfig: () => _globalConfig,
};

// --- Provider ---

interface MetricShiftProviderProps {
  config: MetricShiftConfig;
  children: React.ReactNode;
}

export const MetricShiftProvider: React.FC<MetricShiftProviderProps> = ({
  config,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const engineRef = useRef<TriggerEngine | null>(null);

  const widget: WidgetConfig = config.widget ?? {};
  const trigger = widget.trigger ?? { type: 'manual' };
  const session = widget.session ?? {};

  // Initialise global config on mount
  useEffect(() => {
    MetricShift.init(config);
  }, []);

  // Subscribe to eventBus
  useEffect(() => {
    const unsub = eventBus.on((event) => {
      if (event.type === 'open') {
        setIsOpen(true);
      }
      if (event.type === 'close') {
        setIsOpen(false);
        engineRef.current?.reset();
      }
    });
    return unsub;
  }, []);

  // Start trigger engine
  useEffect(() => {
    const engine = new TriggerEngine(config.projectId, trigger, session);
    engineRef.current = engine;
    engine.start();
    return () => engine.destroy();
  }, [config.projectId]);

  const openWidget = (flowId?: string) => {
    eventBus.emit({ type: 'open', payload: { flowId } });
  };

  const closeWidget = () => {
    eventBus.emit({ type: 'close' });
  };

  return (
    <MetricShiftContext.Provider
      value={{ config, isOpen, openWidget, closeWidget }}
    >
      {children}
    </MetricShiftContext.Provider>
  );
};